/* eslint-disable */
import {
  FC, InputHTMLAttributes, forwardRef, MouseEvent, useState, useEffect,
} from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { CgClose } from 'react-icons/cg';
import classNames from 'classnames';
import {
  AsYouType,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/max';
import type { CountryCode as LibCountryCode } from 'libphonenumber-js/core';
import ReactCountryFlag from 'react-country-flag';
import Tooltip from './Tooltip';
import ErrorMessage from './ErrorMessage';
import Select from './Select/Select';
import {
  countryCodes, defaultCountryCode, getCountryByCode, type CountryCode as CountryMeta,
} from '../../utils/countryCodes';

interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  errMsg?: string;
  tooltipText?: string;
  labelAbove?: boolean;
  textWhite?: boolean;
  additionalClasses?: string;
  isSearch?: boolean;
  placeholder?: string;
  alignRight?: boolean;
  quickDelete?: boolean;
}

const Input: FC<CustomInputProps> = forwardRef<
HTMLInputElement,
CustomInputProps
>(({ id, label, errMsg, type, tooltipText, labelAbove, textWhite, additionalClasses, isSearch = false, placeholder, alignRight = false, quickDelete = false, onChange, value, name, ...rest }, ref) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryMeta>(
    getCountryByCode(defaultCountryCode) || countryCodes[0],
  );
  const [displayNumber, setDisplayNumber] = useState<string>('');
  const [e164Value, setE164Value] = useState<string>('');

  // Initialize phone number from value if provided
  useEffect(() => {
    if (type === 'phone' && value) {
      const stringValue = String(value);
      const parsed = parsePhoneNumberFromString(stringValue);
      if (parsed?.country) {
        const c = getCountryByCode(parsed.country);
        if (c) setSelectedCountry(c);
        const formatter = new AsYouType(parsed.country as unknown as LibCountryCode);
        const national = parsed.nationalNumber || '';
        // Hide the international-only "9" for AR mobiles in the UI
        const nationalForDisplay = (parsed.country === 'AR' && national.startsWith('9'))
          ? national.slice(1)
          : national;
        setDisplayNumber(formatter.input(nationalForDisplay));
        setE164Value(parsed.number);
        return;
      }
      // Fallback: try to format raw digits with current country (do not emit value)
      const digits = stringValue.replace(/\D/g, '');
      const formatter = new AsYouType(selectedCountry.code as unknown as LibCountryCode);
      setDisplayNumber(formatter.input(digits));
      setE164Value('');
    }
  }, [type, value, selectedCountry.code]);

  const commonClasses = 'text-text-primary p-3 border border-transparent hover:border-border-secondary';
  const focusClasses = 'focus:ring-1 focus:outline-none focus:ring-primary-500/20  transition-all duration-200';
  const textInputClasses = classNames(
    commonClasses,
    focusClasses,
    'w-full',
    {
      'border-red-500': errMsg,
      'text-white': textWhite,
      'bg-surface-base': !rest.disabled,
      'bg-surface-secondary': rest.disabled,
    },
    'border-1',
    'border-border-tertiary',
    'dark:border-gradient-brand',
    'button-shadow',
    'rounded-3xl',
    'hidden-number-arrows',
    'themed-date-input',
    additionalClasses,
  );

  const handleClear = (e: MouseEvent<SVGElement>) => {
    if (!onChange) {
      return;
    }

    if (type === 'phone') {
      setDisplayNumber('');
      setE164Value('');
    }

    const mockEvent = { 
      ...e,
      target: {
        ...e.target,
        value: '',
        name: name || id,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    onChange(mockEvent);
  };

  const handleCountrySelect = (country: CountryMeta) => {
    setSelectedCountry(country);

    // Reformat current digits and emit combined value
    const digits = displayNumber.replace(/\D/g, '');
    const formatter = new AsYouType(country.code as unknown as LibCountryCode);
    const formatted = formatter.input(digits);
    setDisplayNumber(formatted);

    const parsed = parsePhoneNumberFromString(digits, { defaultCountry: country.code as unknown as LibCountryCode });
    let emitted = parsed?.isValid() ? parsed.number : '';
    if (parsed?.isValid() && parsed.country === 'AR') {
      if (emitted.startsWith('+54') && !emitted.startsWith('+549')) {
        emitted = emitted.replace('+54', '+549');
      }
    }
    setE164Value(emitted);
    if (onChange) {
      const mockEvent = {
        target: {
          value: emitted,
          name: name || id,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(mockEvent);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawDigits = e.target.value.replace(/\D/g, ''); // Remove non-digits
    // For AR, do not show a leading "9" to the user (it's an international artifact)
    const digitsForDisplay = (selectedCountry.code === 'AR' && rawDigits.startsWith('9'))
      ? rawDigits.slice(1)
      : rawDigits;
    const formatter = new AsYouType(selectedCountry.code as unknown as LibCountryCode);
    const formatted = formatter.input(digitsForDisplay);
    setDisplayNumber(formatted);

    // Emit E.164 if valid, else fallback to calling code + digits
    const parsed = parsePhoneNumberFromString(digitsForDisplay, { defaultCountry: selectedCountry.code as unknown as LibCountryCode });
    let emitted = parsed?.isValid() ? parsed.number : '';
    if (parsed?.isValid() && parsed.country === 'AR') {
      if (emitted.startsWith('+54') && !emitted.startsWith('+549')) {
        emitted = emitted.replace('+54', '+549');
      }
    }
    setE164Value(emitted);
    if (onChange) {
      const event = {
        target: {
          value: emitted,
          name: name || id,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
  };

  // Add a class to hide number input arrows
  const hideNumberArrowsClass = type === 'number' ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' : '';

  return (
    <div className="form-control flex flex-col w-full relative">
      {labelAbove && (
        <div className="flex items-center w-full">
          <label htmlFor={id} className="text-left font-sm font-medium mb-1">
            {label}
          </label>
          {tooltipText && (
            <Tooltip content={tooltipText}>
              <AiOutlineQuestionCircle className="ml-1" />
            </Tooltip>
          )}
        </div>
      )}
      <div className="relative flex items-center">
        {isSearch && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary h-5 w-5 pointer-events-none"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l5.272 5.272a1 1 0 01-1.414 1.414l-5.272-5.272A6 6 0 012 8z" />
          </svg>
        )}
        <div className="flex flex-col gap-2 w-full">
          {type === 'phone' ? (
            <div className="flex items-stretch gap-2 w-full">
              <div className="w-40">
                <Select
                  id={`${id}-country`}
                  options={countryCodes.map((c) => ({
                    value: c.code,
                    label: (
                      <>
                        <ReactCountryFlag
                          svg
                          countryCode={c.code}
                          aria-label={c.name}
                          style={{ width: '1.1rem', height: '1.1rem', borderRadius: '2px' }}
                        />
                        <span className="text-text-primary">{c.dialCode}</span>
                      </>
                    ),
                  }))}
                  value={selectedCountry.code}
                  onChange={(e) => {
                    const next = getCountryByCode(String(e.target.value));
                    if (next) handleCountrySelect(next);
                  }}
                />
              </div>
              <input
                ref={ref}
                type="tel"
                id={id}
                name={name || id}
                placeholder={placeholder || label}
                value={displayNumber}
                className={classNames(
                  textInputClasses,
                  isSearch ? 'pl-10' : '',
                  alignRight ? 'pl-8' : '',
                  hideNumberArrowsClass,
                )}
                {...rest}
                onChange={handlePhoneNumberChange}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
          ) : (
            <input
              ref={ref}
              type={type}
              id={id}
              name={name || id}
              placeholder={placeholder || label}
              value={value}
              className={classNames(
                textInputClasses,
                isSearch ? 'pl-10' : '',
                alignRight ? 'pl-8' : '',
                hideNumberArrowsClass,
              )}
              {...rest}
              onChange={onChange}
              onWheel={(e) => e.currentTarget.blur()}
            />
          )}
          {errMsg && (
            <div className="text-xs mt-1 w-full">
              <ErrorMessage message={errMsg} />
            </div>
          )}
        </div>
        {quickDelete && (
          <CgClose
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 cursor-pointer z-20 text-text-primary"
            onClick={handleClear}
          />
        )}

      </div>
    </div>
  );
});

export default Input;
